import { useState } from "react";
import {
  GripVertical, ArrowUp, ArrowDown, Trash2, Type, Image as ImageIcon,
  Video, Presentation, FlaskConical, Loader2, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import Uploader from "@/components/Uploader";
import VideoEmbed from "@/components/VideoEmbed";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const LABELS = { text: "Texto", image: "Imagen", video: "Vídeo", powerpoint: "PowerPoint", practice: "Problema de práctica" };

const mk = (type) => {
  const base = { id: uid(), type };
  if (type === "text") return { ...base, html: "" };
  if (type === "image") return { ...base, url: "", caption: "", align: "center", width: 100 };
  if (type === "video") return { ...base, url: "" };
  if (type === "powerpoint") return { ...base, url: "", name: "" };
  if (type === "practice") return { ...base, problem: "", solution: "" };
  return base;
};

export default function BlockEditor({ value = [], onChange, allowPractice = false }) {
  const [dragIdx, setDragIdx] = useState(null);
  const blocks = value;
  const setBlocks = (b) => onChange(b);
  const update = (i, patch) => setBlocks(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const add = (type) => setBlocks([...blocks, mk(type)]);
  const remove = (i) => setBlocks(blocks.filter((_, j) => j !== i));
  const move = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const arr = [...blocks];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    setBlocks(arr);
  };
  const onDrop = (i) => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); };

  return (
    <div data-testid="block-editor">
      <div className="space-y-4">
        {blocks.length === 0 && (
          <div className="border border-dashed border-paper-300 bg-paper-50 p-8 text-center text-ink-500 text-sm">
            Aún no hay bloques. Añade Texto, Imagen, Vídeo o PowerPoint abajo.
          </div>
        )}
        {blocks.map((b, i) => (
          <div
            key={b.id}
            data-testid={`block-${i}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className={`bg-white border border-paper-200 ${dragIdx === i ? "opacity-50 ring-2 ring-nasa-blue" : ""}`}
          >
            <div className="flex items-center gap-2 bg-paper-50 border-b border-paper-200 px-3 py-2">
              <span
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnd={() => setDragIdx(null)}
                className="cursor-grab active:cursor-grabbing text-ink-400 hover:text-nasa-blue"
                data-testid={`block-drag-${i}`}
                title="Arrastra para reordenar"
              >
                <GripVertical size={16} />
              </span>
              <span className="eyebrow text-nasa-blue text-[11px]">{LABELS[b.type] || b.type} · {i + 1}</span>
              <div className="ml-auto flex items-center gap-1">
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="p-1 text-ink-500 hover:text-nasa-blue disabled:opacity-30" data-testid={`block-up-${i}`}><ArrowUp size={15} /></button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === blocks.length - 1} className="p-1 text-ink-500 hover:text-nasa-blue disabled:opacity-30" data-testid={`block-down-${i}`}><ArrowDown size={15} /></button>
                <button type="button" onClick={() => remove(i)} className="p-1 text-nasa-red hover:text-nasa-reddark" data-testid={`block-del-${i}`}><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="p-4">
              {b.type === "text" && (
                <RichTextEditor value={b.html} onChange={(v) => update(i, { html: v })} placeholder="Escribe aquí… Usa $x^2$ para fórmulas." />
              )}
              {b.type === "image" && <ImageBlockEditor b={b} i={i} update={update} />}
              {b.type === "video" && (
                <div>
                  <Input value={b.url} onChange={(e) => update(i, { url: e.target.value })} placeholder="https://youtube.com/watch?v=…" data-testid={`block-video-url-${i}`} />
                  <div className="mt-2"><Uploader value={b.url?.startsWith("/api") ? b.url : ""} onChange={(v) => update(i, { url: v })} accept="video/*" label="O subir vídeo" testid={`block-video-upload-${i}`} /></div>
                  {b.url && <div className="mt-3"><VideoEmbed url={b.url} /></div>}
                </div>
              )}
              {b.type === "powerpoint" && <PptBlockEditor b={b} i={i} update={update} />}
              {b.type === "practice" && (
                <div>
                  <label className="eyebrow text-ink-500 block mb-2">Enunciado</label>
                  <RichTextEditor value={b.problem} onChange={(v) => update(i, { problem: v })} placeholder="Enunciado del problema…" />
                  <label className="eyebrow text-ink-500 block mb-2 mt-4">Solución</label>
                  <RichTextEditor value={b.solution} onChange={(v) => update(i, { solution: v })} placeholder="Solución paso a paso…" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-5 border-t border-paper-200 pt-5">
        <span className="eyebrow text-ink-400 w-full mb-1">Añadir bloque</span>
        <AddBtn onClick={() => add("text")} icon={Type} label="Texto" testid="add-block-text" />
        <AddBtn onClick={() => add("image")} icon={ImageIcon} label="Imagen" testid="add-block-image" />
        <AddBtn onClick={() => add("video")} icon={Video} label="Vídeo" testid="add-block-video" />
        <AddBtn onClick={() => add("powerpoint")} icon={Presentation} label="PowerPoint" testid="add-block-powerpoint" />
        {allowPractice && <AddBtn onClick={() => add("practice")} icon={FlaskConical} label="Problema" testid="add-block-practice" />}
      </div>
    </div>
  );
}

function AddBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <Button type="button" variant="outline" onClick={onClick} data-testid={testid} className="border-paper-300 rounded-full text-sm">
      <Icon size={15} className="mr-1.5" /> {label}
    </Button>
  );
}

function ImageBlockEditor({ b, i, update }) {
  const aligns = [["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]];
  return (
    <div>
      <Uploader value={b.url} onChange={(v) => update(i, { url: v })} testid={`block-image-${i}`} label="Subir imagen" />
      <Input value={b.caption} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Pie de foto (opcional)" className="mt-3" data-testid={`block-image-caption-${i}`} />
      <div className="flex flex-wrap items-center gap-4 mt-3">
        <div className="flex items-center gap-1">
          {aligns.map(([a, Ic]) => (
            <button
              type="button"
              key={a}
              onClick={() => update(i, { align: a })}
              data-testid={`block-image-align-${a}-${i}`}
              title={a}
              className={`p-2 rounded border ${b.align === a ? "bg-nasa-blue text-white border-nasa-blue" : "border-paper-300 text-ink-500 hover:border-nasa-blue"}`}
            >
              <Ic size={15} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[180px]">
          <span className="text-xs text-ink-500">Tamaño</span>
          <input type="range" min="25" max="100" value={b.width || 100} onChange={(e) => update(i, { width: Number(e.target.value) })} className="flex-1" data-testid={`block-image-width-${i}`} />
          <span className="text-xs text-ink-500 w-10 text-right">{b.width || 100}%</span>
        </div>
      </div>
    </div>
  );
}

function PptBlockEditor({ b, i, update }) {
  const [loading, setLoading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pptx?|odp)$/i.test(file.name)) { toast.error("Sube un archivo .ppt, .pptx u .odp"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, { url: data.url, name: file.name });
      toast.success("Presentación subida");
    } catch { toast.error("No se pudo subir la presentación"); }
    finally { setLoading(false); e.target.value = ""; }
  };
  return b.url ? (
    <div className="flex items-center justify-between gap-3 bg-paper-50 border border-paper-200 p-4" data-testid={`block-ppt-current-${i}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Presentation size={18} className="text-nasa-blue shrink-0" />
        <span className="text-sm font-medium break-words">{b.name || "Presentación cargada"}</span>
      </div>
      <button type="button" onClick={() => update(i, { url: "", name: "" })} className="text-nasa-red hover:text-nasa-reddark shrink-0" data-testid={`block-ppt-remove-${i}`}><Trash2 size={16} /></button>
    </div>
  ) : (
    <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-paper-300 rounded-lg p-8 cursor-pointer hover:border-nasa-blue transition-colors" data-testid={`block-ppt-dropzone-${i}`}>
      {loading ? <Loader2 className="animate-spin text-nasa-blue" /> : <Presentation size={28} className="text-ink-400" />}
      <span className="text-sm text-ink-500">{loading ? "Subiendo…" : "Subir presentación (.ppt, .pptx)"}</span>
      <input type="file" accept=".ppt,.pptx,.odp" className="hidden" onChange={upload} data-testid={`block-ppt-input-${i}`} disabled={loading} />
    </label>
  );
}
